/* ==========================================================================
   CONVERSION EXTRAS — bottom island
   Product pages: the island appears only after the real main Add to Cart
   button has passed above the viewport. The native sticky bar remains as a
   fail-safe and is hidden only while the docked island is actually visible.
   ========================================================================== */

(() => {
  class BottomIsland extends HTMLElement {
    connectedCallback() {
      this.abortController = new AbortController();
      this.isProductPage = this.hasAttribute('product-page');
      this.footerInView = false;
      this.islandVisible = null;
      this.scrollFrame = 0;
      this.syncFrame = 0;
      this.productRefreshFrame = 0;

      if (this.isProductPage) this.refreshProductTargets();
      this.setupCartVisibility();
      this.pruneUnavailableControls();
      if (this.hasAttribute('dock-sticky-atc')) {
        this.ensureDockedButton();
      }

      this.setupFooterObserver();

      if (this.isProductPage) {
        this.setupProductVisibility();
      } else {
        // Non-product pages: the island complements the auto-hiding header.
        // Hidden at the top and on scroll up (the header is present then),
        // shown on scroll down, and it yields while the footer is visible.
        this.setupDirectionVisibility();
      }

      if (this.classList.contains('bottom-island--glass')) {
        this.setupGlassTracking();
      }

      if (this.hasAttribute('dock-sticky-atc')) {
        const finishDockedLoading = () => this.finishDockedLoading();

        // The theme dispatches these events after the product request has
        // either completed successfully or failed. Resetting from the events
        // makes the island independent from product-section rerenders and
        // prevents its button-content animation from remaining visually stuck.
        document.addEventListener('variant:add', finishDockedLoading, {
          signal: this.abortController.signal,
        });
        document.addEventListener('cart:change', finishDockedLoading, {
          signal: this.abortController.signal,
        });
        document.addEventListener('cart:error', finishDockedLoading, {
          signal: this.abortController.signal,
        });
        document.addEventListener('cart:refresh', finishDockedLoading, {
          signal: this.abortController.signal,
        });
      }
    }

    disconnectedCallback() {
      this.abortController?.abort();
      this.footerObserver?.disconnect();
      this.cartVisibilityObserver?.disconnect();
      this.primaryAtcObserver?.disconnect();
      this.productMutationObserver?.disconnect();
      this.dockedLoadingObserver?.disconnect();

      if (this.scrollFrame) cancelAnimationFrame(this.scrollFrame);
      if (this.directionFrame) cancelAnimationFrame(this.directionFrame);
      if (this.syncFrame) cancelAnimationFrame(this.syncFrame);
      if (this.productRefreshFrame) cancelAnimationFrame(this.productRefreshFrame);
      if (this.glassFrame) cancelAnimationFrame(this.glassFrame);
      if (this.dockedLoadingFallback) window.clearTimeout(this.dockedLoadingFallback);

      document.documentElement.classList.remove('island-atc-active');
    }

    /* The detached cart circle only exists while there is something in the
       cart. The theme's own <cart-count> element is the source of truth: it
       already toggles its hidden attribute on every cart change (including
       back/forward restores), so the circle simply mirrors it — with a
       small pop when the first item arrives. */
    setupCartVisibility() {
      this.cartLink = this.querySelector('.bottom-island__cart');
      const count = this.cartLink?.querySelector('cart-count');
      if (!this.cartLink || !count) return;

      const sync = () => {
        const empty = count.hidden;
        if (this.cartLink.hidden === empty) return;

        this.cartLink.hidden = empty;

        if (!empty) {
          this.cartLink.classList.remove('is-appearing');
          void this.cartLink.offsetWidth; // restart the pop animation
          this.cartLink.classList.add('is-appearing');

          // A finished fill-mode animation would pin transform/opacity and
          // silence the island's own entrance/exit motion — release it.
          this.cartLink.addEventListener(
            'animationend',
            () => this.cartLink?.classList.remove('is-appearing'),
            { once: true }
          );
        }
      };

      sync();

      if ('MutationObserver' in window) {
        this.cartVisibilityObserver = new MutationObserver(sync);
        this.cartVisibilityObserver.observe(count, {
          attributes: true,
          attributeFilter: ['hidden'],
        });
      }

      // Direct fallback in case the theme's cart-count element ever fails
      // to register: the event payload carries the item count.
      document.addEventListener(
        'cart:change',
        (event) => {
          const itemCount = event.detail?.cart?.item_count;
          if (typeof itemCount !== 'number') return;
          count.hidden = itemCount === 0;
          sync();
        },
        { signal: this.abortController.signal }
      );
    }

    setupGlassTracking() {
      if (!window.matchMedia('(pointer: fine)').matches) return;
      if (window.matchMedia('(prefers-reduced-motion: reduce)').matches) return;

      this.addEventListener('pointermove', (event) => {
        const surface = event.target.closest('.bottom-island__surface, .bottom-island__cart');
        if (!surface || !this.contains(surface)) return;

        this.pendingGlassPointer = { surface, clientX: event.clientX, clientY: event.clientY };
        if (this.glassFrame) return;

        this.glassFrame = requestAnimationFrame(() => {
          this.glassFrame = 0;
          const pointer = this.pendingGlassPointer;
          if (!pointer?.surface?.isConnected) return;

          if (this.activeGlassSurface && this.activeGlassSurface !== pointer.surface) {
            this.resetGlassSurface(this.activeGlassSurface);
          }

          const rect = pointer.surface.getBoundingClientRect();
          const x = Math.max(0, Math.min(100, ((pointer.clientX - rect.left) / rect.width) * 100));
          const y = Math.max(0, Math.min(100, ((pointer.clientY - rect.top) / rect.height) * 100));

          pointer.surface.style.setProperty('--glass-pointer-x', `${x}%`);
          pointer.surface.style.setProperty('--glass-pointer-y', `${y}%`);
          this.activeGlassSurface = pointer.surface;
        });
      }, { signal: this.abortController.signal, passive: true });

      this.addEventListener('pointerleave', () => {
        this.resetGlassSurface(this.activeGlassSurface);
        this.activeGlassSurface = null;
      }, { signal: this.abortController.signal });
    }

    resetGlassSurface(surface) {
      if (!surface) return;
      surface.style.removeProperty('--glass-pointer-x');
      surface.style.removeProperty('--glass-pointer-y');
    }

    pruneUnavailableControls() {
      this.querySelectorAll('[aria-controls]').forEach((control) => {
        if (document.getElementById(control.getAttribute('aria-controls'))) return;

        if (control.tagName === 'A') {
          control.removeAttribute('aria-controls');
        } else {
          control.remove();
        }
      });
    }

    setupFooterObserver() {
      const footer = document.querySelector('.shopify-section--footer');
      if (!footer || !('IntersectionObserver' in window)) return;

      this.footerObserver = new IntersectionObserver(([entry]) => {
        this.footerInView = entry.isIntersecting;

        if (this.isProductPage) {
          this.queueProductVisibility();
        } else {
          this.syncDirectionVisibility();
        }
      });

      this.footerObserver.observe(footer);
    }

    /* --------------------------------------------------------------------
       Direction-aware visibility (non-product pages)
       The theme's sticky header hides on scroll down and returns on scroll
       up, so the island fills the gap: it appears only while the header is
       away. At the top and while scrolling up it stays out of the way.
       -------------------------------------------------------------------- */
    setupDirectionVisibility() {
      this.TOP_THRESHOLD = 8;
      this.DIRECTION_THRESHOLD = 12;

      this.resetDirectionState();

      const requestDirectionUpdate = () => {
        if (this.directionFrame) return;
        this.directionFrame = requestAnimationFrame(() => {
          this.directionFrame = 0;
          this.updateDirectionVisibility();
        });
      };

      window.addEventListener('scroll', requestDirectionUpdate, {
        signal: this.abortController.signal,
        passive: true,
      });
      window.addEventListener('resize', requestDirectionUpdate, {
        signal: this.abortController.signal,
        passive: true,
      });
      /* Restored pages (back/forward cache) have no trustworthy direction
         yet: start hidden and wait for the first clear downward movement. */
      window.addEventListener('pageshow', () => this.resetDirectionState(), {
        signal: this.abortController.signal,
      });
      document.addEventListener('shopify:section:load', () => this.resetDirectionState(), {
        signal: this.abortController.signal,
      });
    }

    resetDirectionState() {
      this.lastScrollY = this.getScrollY();
      this.activeDirection = 0;
      this.directionTravelled = 0;
      this.directionWantsVisible = false;
      this.syncDirectionVisibility();
    }

    getScrollY() {
      return Math.max(0, window.scrollY || document.documentElement.scrollTop || 0);
    }

    updateDirectionVisibility() {
      const currentY = this.getScrollY();

      if (currentY <= this.TOP_THRESHOLD) {
        this.lastScrollY = currentY;
        this.activeDirection = 0;
        this.directionTravelled = 0;
        this.directionWantsVisible = false;
        this.syncDirectionVisibility();
        return;
      }

      const delta = currentY - this.lastScrollY;
      this.lastScrollY = currentY;

      if (Math.abs(delta) < 0.5) return;

      const direction = delta > 0 ? 1 : -1;

      if (direction !== this.activeDirection) {
        this.activeDirection = direction;
        this.directionTravelled = 0;
      }

      this.directionTravelled += Math.abs(delta);
      if (this.directionTravelled < this.DIRECTION_THRESHOLD) return;

      this.directionTravelled = 0;
      this.directionWantsVisible = direction > 0;
      this.syncDirectionVisibility();
    }

    syncDirectionVisibility() {
      if (this.isProductPage) return;
      this.setVisible(this.directionWantsVisible && !this.footerInView);
    }

    setupProductVisibility() {
      this.setVisible(false);
      this.observePrimaryAtc();

      const update = () => this.queueProductVisibility();
      window.addEventListener('scroll', update, {
        signal: this.abortController.signal,
        passive: true,
      });
      window.addEventListener('resize', update, {
        signal: this.abortController.signal,
        passive: true,
      });
      window.addEventListener('pageshow', update, { signal: this.abortController.signal });
      document.addEventListener('shopify:section:load', update, {
        signal: this.abortController.signal,
      });

      const productSection = document.querySelector('.shopify-section--main-product');

      if (productSection) {
        this.productMutationObserver = new MutationObserver(() => {
          this.queueProductRefresh();
        });

        this.productMutationObserver.observe(productSection, {
          childList: true,
          subtree: true,
          characterData: true,
          attributes: true,
          attributeFilter: ['disabled', 'aria-disabled'],
        });
      }

      this.queueProductVisibility();
    }

    refreshProductTargets() {
      const stickyFormId = this.stickyAtc?.getAttribute('main-product-form');
      this.stickyAtc =
        document.querySelector('product-sticky-add-to-cart[main-product-form]') || this.stickyAtc || null;

      const currentStickyFormId = this.stickyAtc?.getAttribute('main-product-form') || stickyFormId;
      this.mainProductForm =
        (currentStickyFormId && document.getElementById(currentStickyFormId)) ||
        document.querySelector('.shopify-section--main-product form[id^="product-form-main-"]') ||
        document.querySelector('main form[id^="product-form-main-"]') ||
        null;

      this.primaryAtc = this.findPrimaryAtc(this.mainProductForm);
    }

    getMainProductForm() {
      if (!this.mainProductForm?.isConnected) this.refreshProductTargets();
      return this.mainProductForm;
    }

    findPrimaryAtc(form) {
      if (!form) return null;

      const preferred =
        form.querySelector('.main-product-atc') ||
        form.querySelector('buy-buttons button[type="submit"]:not(.shopify-payment-button__button)') ||
        form.querySelector('.buy-buttons button[type="submit"]:not(.shopify-payment-button__button)');

      if (preferred) return preferred;

      return (
        Array.from(form.querySelectorAll('button[type="submit"]')).find(
          (button) =>
            !button.closest('.shopify-payment-button') &&
            !button.classList.contains('shopify-payment-button__button')
        ) || null
      );
    }

    getPrimaryAtc() {
      const form = this.getMainProductForm();

      if (this.primaryAtc?.isConnected && form?.contains(this.primaryAtc)) {
        return this.primaryAtc;
      }

      this.primaryAtc = this.findPrimaryAtc(form);
      return this.primaryAtc;
    }

    observePrimaryAtc() {
      const primaryAtc = this.getPrimaryAtc();
      if (primaryAtc === this.observedPrimaryAtc) return;

      this.primaryAtcObserver?.disconnect();
      this.observedPrimaryAtc = primaryAtc;

      if (!primaryAtc || !('IntersectionObserver' in window)) {
        this.queueProductVisibility();
        return;
      }

      this.primaryAtcObserver = new IntersectionObserver(
        () => this.queueProductVisibility(),
        { threshold: [0, 0.01, 1] }
      );
      this.primaryAtcObserver.observe(primaryAtc);
    }

    queueProductVisibility() {
      if (this.scrollFrame) return;

      this.scrollFrame = requestAnimationFrame(() => {
        this.scrollFrame = 0;
        this.updateProductVisibility();
      });
    }

    updateProductVisibility() {
      if (!this.primaryAtc?.isConnected) {
        this.refreshProductTargets();
        this.observePrimaryAtc();
      }

      if (this.hasAttribute('dock-sticky-atc') && !this.hasDockedAtc) {
        this.ensureDockedButton();
      }

      const primaryAtc = this.getPrimaryAtc();

      if (!primaryAtc || !this.hasDockedAtc) {
        this.setVisible(false);
        return;
      }

      const rect = primaryAtc.getBoundingClientRect();
      const rendered = primaryAtc.isConnected && rect.width > 0 && rect.height > 0;
      const inViewport =
        rendered && rect.bottom > 0 && rect.top < (window.innerHeight || document.documentElement.clientHeight);
      const hasPassedAboveViewport = rendered && rect.bottom <= 0;

      // Never duplicate the real buy button. The island appears only after the
      // main ATC has gone above the viewport, and disappears immediately when
      // that button comes back into view or the footer is reached.
      this.setVisible(hasPassedAboveViewport && !inViewport && !this.footerInView);
    }

    setVisible(visible) {
      visible = Boolean(visible);
      if (this.islandVisible === visible) return;

      this.islandVisible = visible;
      this.classList.toggle('is-visible', visible);
      this.setAttribute('aria-hidden', visible ? 'false' : 'true');
      this.toggleAttribute('inert', !visible);

      if (this.hasDockedAtc) {
        document.documentElement.classList.toggle('island-atc-active', visible);
      } else if (!visible) {
        document.documentElement.classList.remove('island-atc-active');
      }
    }

    ensureDockedButton() {
      if (!this.hasAttribute('dock-sticky-atc')) return false;

      const slot = this.querySelector('[data-island-atc-slot]');
      const form = this.getMainProductForm();

      if (!slot || !form) return false;

      if (!this.dockedButton) {
        const button = document.createElement('button');
        const buttonContent = document.createElement('button-content');

        // This is deliberately a regular button. Relying on an externally
        // associated submit button was inconsistent after Shopify product
        // rerenders. We explicitly submit through the real main product button
        // instead, so the theme's native product-form handler always runs.
        button.type = 'button';
        button.className = 'button button--sm';
        buttonContent.className = 'button__content';
        button.append(buttonContent);
        slot.replaceChildren(button);
        this.dockedButton = button;
        this.dockedButtonContent = buttonContent;
        this.hasDockedAtc = true;
        this.classList.add('has-docked-atc');

        button.addEventListener(
          'click',
          (event) => this.onDockedButtonClick(event),
          { signal: this.abortController.signal }
        );
      }

      if (!this.syncDockedButton) {
        this.syncDockedButton = () => {
          const mainForm = this.getMainProductForm();
          const productInfo = mainForm?.closest('.product-info') ||
            mainForm?.closest('product-rerender')?.querySelector('.product-info');
          // Always use the real main product CTA as the source of truth. It
          // owns the selected variant, disabled state and native loading state.
          const sourceButton = this.getPrimaryAtc() ||
            this.stickyAtc?.querySelector('button[type="submit"]');
          const label = sourceButton?.textContent.trim() || '';
          const sourceButtonContent = sourceButton?.querySelector('button-content');

          this.observeDockedLoadingSource(sourceButton);

          if (!this.dockedButtonContent?.isConnected) {
            this.dockedButtonContent = document.createElement('button-content');
            this.dockedButtonContent.className = 'button__content';
            this.dockedButton.replaceChildren(this.dockedButtonContent);
          }

          // Keep the same button animation configuration as the theme's
          // native Add to Cart button. The product form toggles aria-busy on
          // every associated submit button, and <button-content> turns that
          // state into the existing loading transition.
          ['rotated-text', 'up-text', 'glowing'].forEach((attribute) => {
            if (sourceButtonContent?.hasAttribute(attribute)) {
              this.dockedButtonContent.setAttribute(
                attribute,
                sourceButtonContent.getAttribute(attribute) || ''
              );
            } else {
              this.dockedButtonContent.removeAttribute(attribute);
            }
          });

          const setDockedButtonLabel = (text) => {
            this.dockedButtonContent.textContent = text;

            if (this.dockedButtonContent.hasAttribute('rotated-text')) {
              this.dockedButtonContent.setAttribute('rotated-text', text);
            }

            if (this.dockedButtonContent.hasAttribute('up-text')) {
              this.dockedButtonContent.setAttribute('up-text', text);
            }
          };

          const bisTrigger =
            this.stickyAtc?.querySelector(
              '.klaviyo-bis-trigger, a[href*="klaviyo-bis"], button[class*="klaviyo"]'
            ) ||
            productInfo?.querySelector(
              '.klaviyo-bis-trigger, a[href*="klaviyo-bis"], button[class*="klaviyo"]'
            );

          this.syncDockIdentity(productInfo);

          if (sourceButton?.disabled && bisTrigger) {
            this.dockedAction = 'notify';
            this.dockedBisTrigger = bisTrigger;
            this.dockedButton.disabled = false;
            this.dockedButton.removeAttribute('aria-busy');
            setDockedButtonLabel(bisTrigger.textContent.trim() || 'Notify me');
          } else {
            this.dockedAction = 'add';
            this.dockedBisTrigger = null;
            this.dockedButton.disabled = Boolean(sourceButton?.disabled);
            setDockedButtonLabel(label || this.getAttribute('atc-label') || 'Add to cart');
          }
        };
      }

      this.syncDockedButton();
      return true;
    }


    /* Reads the selected variant's current price from the theme's own
       rerendered price lists (sticky ATC first, main product info as a
       fallback), skipping screen-reader prefixes and struck compare-at
       prices so only the amount the shopper pays is restated. */
    readDockPrice(productInfo) {
      const priceList =
        this.stickyAtc?.querySelector('price-list') ||
        productInfo?.querySelector('price-list') ||
        document.querySelector('.product-info price-list');

      const salePrice = priceList?.querySelector('sale-price');
      if (!salePrice) return '';

      let text = '';
      salePrice.childNodes.forEach((node) => {
        if (node.nodeType === Node.ELEMENT_NODE && node.classList.contains('sr-only')) return;
        text += node.textContent;
      });

      return text.replace(/\s+/g, ' ').trim();
    }

    /* Keeps the dock's product thumbnail and price line on the selected
       variant by mirroring the theme's rerendered sticky ATC content. */
    syncDockIdentity(productInfo) {
      const priceTarget = this.querySelector('[data-island-identity-price]');

      if (priceTarget) {
        const price = this.readDockPrice(productInfo);
        if (price && priceTarget.textContent !== price) {
          priceTarget.textContent = price;
        }
      }

      const sizeTarget = this.querySelector('[data-island-identity-size]');
      const priceLine = this.querySelector('[data-island-identity-price-line]');

      if (sizeTarget && priceLine) {
        const sizeIndex = parseInt(priceLine.dataset.sizeIndex, 10);
        const variantTitle = this.stickyAtc
          ?.querySelector('.text-subdued')
          ?.textContent.trim();

        if (sizeIndex >= 0 && variantTitle) {
          const value = variantTitle.split(' / ')[sizeIndex] || variantTitle;
          const next = ` \u00B7 ${value}`;
          if (sizeTarget.textContent !== next) sizeTarget.textContent = next;
        }
      }

      const identityImage = this.querySelector('.bottom-island__identity-image');
      if (!identityImage) return;

      const sourceImage = this.stickyAtc?.querySelector('img');
      if (!sourceImage?.getAttribute('src')) return;

      const src = sourceImage.getAttribute('src');
      if (identityImage.getAttribute('src') === src) return;

      identityImage.setAttribute('src', src);

      const srcset = sourceImage.getAttribute('srcset');
      if (srcset) {
        identityImage.setAttribute('srcset', srcset);
        identityImage.setAttribute('sizes', '48px');
      } else {
        identityImage.removeAttribute('srcset');
        identityImage.removeAttribute('sizes');
      }
    }

    observeDockedLoadingSource(sourceButton) {
      if (sourceButton === this.dockedLoadingSource) {
        this.syncDockedLoadingState();
        return;
      }

      this.dockedLoadingObserver?.disconnect();
      this.dockedLoadingSource = sourceButton || null;

      if (!sourceButton || !('MutationObserver' in window)) {
        this.syncDockedLoadingState();
        return;
      }

      this.dockedLoadingObserver = new MutationObserver(() => {
        this.syncDockedLoadingState();
      });
      this.dockedLoadingObserver.observe(sourceButton, {
        attributes: true,
        attributeFilter: ['aria-busy', 'disabled'],
      });
      this.syncDockedLoadingState();
    }

    syncDockedLoadingState() {
      if (!this.dockedButton) return;

      const isBusy = this.dockedLoadingSource?.getAttribute('aria-busy') === 'true';

      if (isBusy) {
        this.dockedButton.setAttribute('aria-busy', 'true');
      } else {
        this.finishDockedLoading();
      }
    }

    finishDockedLoading() {
      if (this.dockedLoadingFallback) {
        window.clearTimeout(this.dockedLoadingFallback);
        this.dockedLoadingFallback = 0;
      }

      if (!this.dockedButton) return;

      const wasBusy = this.dockedButton.getAttribute('aria-busy') === 'true';
      this.dockedButton.removeAttribute('aria-busy');

      if (!wasBusy || !this.dockedButtonContent?.isConnected) return;

      // Recreate only the visual content element after a completed request.
      // The theme's button-content animation can occasionally retain its
      // transient SVG after aria-busy is removed. A fresh element guarantees
      // the normal label is restored while keeping the same native animation
      // for the next click.
      const currentContent = this.dockedButtonContent;
      const replacement = document.createElement('button-content');
      replacement.className = currentContent.className;
      replacement.textContent = currentContent.textContent;

      ['rotated-text', 'up-text', 'glowing'].forEach((attribute) => {
        if (currentContent.hasAttribute(attribute)) {
          replacement.setAttribute(attribute, currentContent.getAttribute(attribute) || '');
        }
      });

      currentContent.replaceWith(replacement);
      this.dockedButtonContent = replacement;
    }

    onDockedButtonClick(event) {
      event.preventDefault();

      if (this.dockedAction === 'notify') {
        this.dockedBisTrigger?.click();
        return;
      }

      const form = this.getMainProductForm();
      const primaryAtc = this.getPrimaryAtc();

      if (!form || !primaryAtc || primaryAtc.disabled) return;
      if (primaryAtc.getAttribute('aria-busy') === 'true') return;

      // Show the exact native three-dot loader immediately. The observer above
      // then keeps this state in sync with the real CTA until Shopify's AJAX
      // add-to-cart request resolves or errors.
      this.dockedButton.setAttribute('aria-busy', 'true');

      if (this.dockedLoadingFallback) {
        window.clearTimeout(this.dockedLoadingFallback);
      }
      this.dockedLoadingFallback = window.setTimeout(() => {
        this.finishDockedLoading();
      }, 12000);

      try {
        if (typeof form.requestSubmit === 'function') {
          form.requestSubmit(primaryAtc);
        } else {
          primaryAtc.click();
        }
      } catch (error) {
        primaryAtc.click();
      }

      // Invalid forms never enter the theme's busy state, so clear the eager
      // loader rather than leaving the island stuck indefinitely.
      window.setTimeout(() => {
        if (primaryAtc.getAttribute('aria-busy') !== 'true') {
          this.finishDockedLoading();
        }
      }, 150);
    }

    queueProductRefresh() {
      if (this.productRefreshFrame) return;

      this.productRefreshFrame = requestAnimationFrame(() => {
        this.productRefreshFrame = 0;
        this.refreshProductTargets();
        this.observePrimaryAtc();
        this.ensureDockedButton();
        this.queueDockSync();
        this.queueProductVisibility();
      });
    }

    queueDockSync() {
      if (!this.syncDockedButton || this.syncFrame) return;

      this.syncFrame = requestAnimationFrame(() => {
        this.syncFrame = 0;
        this.syncDockedButton();
      });
    }

  }

  if (!window.customElements.get('bottom-island')) {
    window.customElements.define('bottom-island', BottomIsland);
  }

  /* ------------------------------------------------------------------------
     DELIVERY COUNTDOWN
     Live "Xh Ym" countdown to the dispatch cutoff (epoch provided server-side
     in the shop's timezone). When the cutoff passes while the page is open,
     the whole primary line is hidden — a reload recomputes fresh dates.
     ------------------------------------------------------------------------ */
  class DeliveryCountdown extends HTMLElement {
    connectedCallback() {
      this.deadline = parseInt(this.getAttribute('deadline'), 10) * 1000;
      this.render();
      this.interval = setInterval(() => this.render(), 30000);
    }

    disconnectedCallback() {
      clearInterval(this.interval);
    }

    render() {
      const remaining = this.deadline - Date.now();

      if (remaining <= 0) {
        clearInterval(this.interval);
        this.closest('.delivery-estimate__primary')?.setAttribute('hidden', '');
        return;
      }

      const totalMinutes = Math.max(1, Math.ceil(remaining / 60000));
      const hours = Math.floor(totalMinutes / 60);
      const minutes = totalMinutes % 60;

      this.textContent =
        hours > 0 ? (minutes > 0 ? `${hours}h ${minutes}m` : `${hours}h`) : `${minutes}m`;
    }
  }

  if (!window.customElements.get('delivery-countdown')) {
    window.customElements.define('delivery-countdown', DeliveryCountdown);
  }
})();
